/**
 * EntityAwareEditor Component
 * Tiptap-based rich text editor with entity detection
 */

import { useEffect, useCallback, useRef, useMemo, useState, forwardRef, useImperativeHandle } from 'react'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { EntityMention, DetectedEntity } from './EntityMentionExtension'
import { EntityTypeFilter } from './EntityTypeFilter'
import { Button } from '@/components/ui/button'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo,
  Redo,
  Sparkles,
  Loader2,
  Wand2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDocumentStore } from '@/stores/documentStore'
import { useEntityDetection } from '@/hooks/useEntityDetection'
import { useDocumentGeneration } from '@/hooks/useDocumentGeneration'
import { SlashCommandMenu, useSlashCommand } from './SlashCommandMenu'
import { DetectedEntityTooltip } from './DetectedEntityTooltip'
import type { SlashCommand, DetectedEntity as DetectedEntityType } from '@/types/document'
import { mapToDisplayEntityType } from '@/types/document'

interface EntityAwareEditorProps {
  documentId: string
  className?: string
}

export interface EntityAwareEditorHandle {
  scrollToEntity: (entityId: string, evidence: string) => void
  scrollToHeading: (headingText: string) => void
}

export const EntityAwareEditor = forwardRef<EntityAwareEditorHandle, EntityAwareEditorProps>(
  function EntityAwareEditor({ documentId, className }, ref) {
  const currentDoc = useDocumentStore((state) =>
    state.documents.find((d) => d.id === documentId)
  )
  const updateContent = useDocumentStore((state) => state.updateContent)
  const visibleEntityTypes = useDocumentStore((state) => state.visibleEntityTypes)
  const toggleEntityTypeVisibility = useDocumentStore((state) => state.toggleEntityTypeVisibility)
  const acceptEntity = useDocumentStore((state) => state.acceptEntity)
  const rejectEntity = useDocumentStore((state) => state.rejectEntity)

  // Tooltip state for detected entity clicks
  const [tooltipEntity, setTooltipEntity] = useState<DetectedEntityType | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)

  const slashCommand = useSlashCommand()
  const editorRef = useRef<Editor | null>(null)
  const prevDocumentIdRef = useRef<string>(documentId)
  const prevStatusesRef = useRef<Map<string, string>>(new Map())

  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: 'Start typing... Use / to insert entities',
      }),
      EntityMention,
      DetectedEntity,
    ],
    content: currentDoc?.content || '<p></p>',
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[400px] px-12 py-10',
      },
    },
    onUpdate: ({ editor }) => {
      const json = editor.getJSON()
      const text = editor.getText()
      updateContent(documentId, json, text)
    },
  })

  editorRef.current = editor

  // Sync editor content when document changes
  useEffect(() => {
    if (!editor) return

    // Check if documentId actually changed
    if (prevDocumentIdRef.current !== documentId) {
      prevDocumentIdRef.current = documentId

      // Load the new document's content
      const newContent = currentDoc?.content || { type: 'doc', content: [{ type: 'paragraph' }] }
      editor.commands.setContent(newContent)

      // Clear previous entity status tracking for the new document
      prevStatusesRef.current = new Map()
    }
  }, [editor, documentId, currentDoc?.content])

  // Entity detection
  const plainText = editor?.getText() || ''
  const { status: _status, isAnalyzing, triggerAnalysis } = useEntityDetection({
    documentId,
    content: plainText,
    enabled: true,
    debounceMs: 3000,
  })

  // AI content generation
  const setContent = useDocumentStore((state) => state.setContent)
  const { generate, isGenerating } = useDocumentGeneration({
    onSuccess: (result) => {
      if (editor && result.tiptapContent) {
        // Update the editor with generated content
        editor.commands.setContent(result.tiptapContent)
        // Also update the store
        setContent(documentId, result.tiptapContent, result.plainText)
      }
    },
    onError: (error) => {
      console.error('Generation failed:', error.message || 'Failed to generate content')
    },
  })

  // Check if document has a template (needed for generation)
  const hasTemplate = !!currentDoc?.templateId

  // Handle AI content generation
  const handleGenerate = useCallback(() => {
    if (!hasTemplate || !currentDoc?.templateId) {
      return  // Button is hidden for non-template docs, but guard anyway
    }
    generate({
      docId: documentId,
      templateId: currentDoc.templateId,
      documentTitle: currentDoc.title,
    })
  }, [generate, documentId, hasTemplate, currentDoc?.templateId, currentDoc?.title])

  // Expose scrollToEntity and scrollToHeading via ref
  useImperativeHandle(ref, () => ({
    scrollToEntity: (entityId: string, evidence: string) => {
      if (!editor || !evidence) return

      // Find the evidence text in the document
      const fullText = editor.getText()
      const index = fullText.indexOf(evidence)

      if (index === -1) return

      // Calculate the position (TipTap positions are 1-indexed)
      const from = index + 1
      const to = from + evidence.length

      // Set selection and focus
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .run()

      // Scroll the selection into view
      const { view } = editor
      const coords = view.coordsAtPos(from)
      const editorElement = view.dom.parentElement

      if (editorElement && coords) {
        // Scroll the editor container to show the selection
        const editorRect = editorElement.getBoundingClientRect()
        const scrollTop = coords.top - editorRect.top - editorElement.offsetHeight / 3
        editorElement.scrollTo({
          top: editorElement.scrollTop + scrollTop,
          behavior: 'smooth',
        })
      }
    },
    scrollToHeading: (headingText: string) => {
      if (!editor) return

      const fullText = editor.getText()
      const index = fullText.indexOf(headingText)

      if (index === -1) return

      const from = index + 1
      const to = from + headingText.length

      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .run()

      // Scroll into view
      const { view } = editor
      const coords = view.coordsAtPos(from)
      const editorElement = view.dom.parentElement

      if (editorElement && coords) {
        const editorRect = editorElement.getBoundingClientRect()
        const scrollTop = coords.top - editorRect.top - 80
        editorElement.scrollTo({
          top: editorElement.scrollTop + scrollTop,
          behavior: 'smooth',
        })
      }
    },
  }), [editor])

  // Get detected entities for highlighting
  const detectedEntities = currentDoc?.detectedEntities || []

  // Compute entity counts by type for filter badges
  const entityCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    detectedEntities.forEach((entity) => {
      // Use mapToDisplayEntityType to ensure consistent type mapping
      const type = mapToDisplayEntityType(entity.type, entity.suggestedOntologyType)
      counts[type] = (counts[type] || 0) + 1
    })
    return counts
  }, [detectedEntities])

  // Highlight detected entities and handle status changes
  useEffect(() => {
    if (!editor) return

    const fullText = editor.getText()
    const prevStatuses = prevStatusesRef.current
    const newStatuses = new Map<string, string>()

    detectedEntities.forEach(entity => {
      const searchText = entity.evidence || entity.content
      if (!searchText || searchText.length < 5) return

      newStatuses.set(entity.id, entity.status)
      const prevStatus = prevStatuses.get(entity.id)

      // Find position of the evidence in the text
      const index = fullText.indexOf(searchText)
      if (index === -1) return

      const from = index + 1
      const to = from + searchText.length

      // If status changed from pending to accepted, convert to entity mention
      if (prevStatus === 'pending' && entity.status === 'accepted') {
        const displayType = mapToDisplayEntityType(entity.type, entity.suggestedOntologyType)
        editor
          .chain()
          .setTextSelection({ from, to })
          .unsetDetectedEntity()
          .setEntityMention({
            entityType: displayType,
          })
          .setTextSelection(to)
          .run()
        return
      }

      // If status changed from pending to rejected, remove highlight
      if (prevStatus === 'pending' && entity.status === 'rejected') {
        editor
          .chain()
          .setTextSelection({ from, to })
          .unsetDetectedEntity()
          .setTextSelection(to)
          .run()
        return
      }

      // If pending and not yet highlighted, add highlight
      if (entity.status === 'pending') {
        const { state } = editor
        let alreadyMarked = false
        state.doc.nodesBetween(from, to, (node) => {
          if (node.marks.some(mark => mark.type.name === 'detectedEntity')) {
            alreadyMarked = true
          }
        })

        if (!alreadyMarked) {
          const displayType = mapToDisplayEntityType(entity.type, entity.suggestedOntologyType)
          editor
            .chain()
            .setTextSelection({ from, to })
            .setDetectedEntity({
              detectedId: entity.id,
              entityType: displayType,
            })
            .setTextSelection(to)
            .run()
        }
      }
    })

    // Update the previous statuses ref
    prevStatusesRef.current = newStatuses
  }, [editor, detectedEntities])

  // Handle slash command detection
  useEffect(() => {
    if (!editor) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Open menu on /
      if (event.key === '/' && !slashCommand.isOpen) {
        const { view } = editor
        const { from } = view.state.selection

        // Get cursor position for menu placement
        const coords = view.coordsAtPos(from)
        // startPos should point to the `/` character itself (from - 1),
        // so textBetween(startPos, from) includes the `/`
        slashCommand.open(
          { top: coords.top, left: coords.left },
          from - 1
        )
        return
      }

      // Update filter while menu is open
      if (slashCommand.isOpen && slashCommand.startPos !== null) {
        const { from } = editor.view.state.selection
        const text = editor.state.doc.textBetween(slashCommand.startPos, from, '')

        // Close if moved away or deleted the /
        if (from < slashCommand.startPos || !text.startsWith('/')) {
          slashCommand.close()
          return
        }

        // Update filter (remove the /)
        slashCommand.updateFilter(text.slice(1))
      }
    }

    // Use capture phase to intercept before Tiptap
    document.addEventListener('keyup', handleKeyDown)
    return () => document.removeEventListener('keyup', handleKeyDown)
  }, [editor, slashCommand])

  // Handle clicks on detected entities to show tooltip
  useEffect(() => {
    if (!editor) return

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      // Check if clicked on a detected entity highlight
      const detectedEl = target.closest('.detected-entity-highlight')
      if (!detectedEl) {
        // Close tooltip if clicking elsewhere
        setTooltipEntity(null)
        setTooltipPosition(null)
        return
      }

      // Get the entity ID from the data attribute
      const entityId = detectedEl.getAttribute('data-detected-id')
      if (!entityId) return

      // Find the entity in our list
      const entity = detectedEntities.find(e => e.id === entityId)
      if (!entity || entity.status !== 'pending') return

      // Get position for tooltip
      const rect = detectedEl.getBoundingClientRect()
      setTooltipPosition({ x: rect.left, y: rect.bottom })
      setTooltipEntity(entity)
    }

    // Add listener to the editor DOM
    const editorDom = editor.view.dom
    editorDom.addEventListener('click', handleClick)

    return () => {
      editorDom.removeEventListener('click', handleClick)
    }
  }, [editor, detectedEntities])

  // Handle command selection
  const handleCommandSelect = useCallback((command: SlashCommand) => {
    if (!editor || slashCommand.startPos === null) return

    const { from } = editor.view.state.selection

    // Delete the slash command text
    editor
      .chain()
      .focus()
      .deleteRange({ from: slashCommand.startPos, to: from })
      .run()

    // Insert entity with proper styling
    const label = prompt(`Enter ${command.name}:`)
    if (label) {
      // Insert text with entityMention mark for visual styling
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          marks: [
            {
              type: 'entityMention',
              attrs: {
                entityType: command.entityType,
              },
            },
          ],
          text: label,
        })
        .insertContent(' ')
        .run()
    }

    slashCommand.close()
  }, [editor, slashCommand])

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-warm-200 bg-warm-50">
        <ToolbarButton
          icon={Bold}
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        />
        <ToolbarButton
          icon={Italic}
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        />
        <div className="w-px h-5 bg-warm-200 mx-1" />
        <ToolbarButton
          icon={Heading1}
          isActive={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Heading 1"
        />
        <ToolbarButton
          icon={Heading2}
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
        />
        <div className="w-px h-5 bg-warm-200 mx-1" />
        <ToolbarButton
          icon={List}
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        />
        <ToolbarButton
          icon={ListOrdered}
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        />
        <div className="w-px h-5 bg-warm-200 mx-1" />
        <ToolbarButton
          icon={Undo}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        />
        <ToolbarButton
          icon={Redo}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        />

        <div className="flex-1" />

        {/* Detection Status */}
        <div className="flex items-center gap-2 text-xs text-text-muted">
          {isAnalyzing ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3" />
              <span>AI Detection Active</span>
            </>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="ml-2 h-7 text-xs"
          onClick={triggerAnalysis}
          disabled={isAnalyzing}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Detect Now
        </Button>

        {/* Generate with AI button - only for template-based documents */}
        {hasTemplate && (
          <Button
            variant="default"
            size="sm"
            className="ml-2 h-7 text-xs gap-1.5 bg-business hover:bg-business-dark"
            onClick={handleGenerate}
            disabled={isGenerating}
            title="Generate content with AI"
          >
            {isGenerating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Wand2 className="w-3 h-3" />
            )}
            {isGenerating ? 'Generating...' : 'Generate with AI'}
          </Button>
        )}
      </div>

      {/* Entity Type Filter */}
      <EntityTypeFilter
        visibleTypes={visibleEntityTypes}
        onToggle={toggleEntityTypeVisibility}
        entityCounts={entityCounts}
      />

      {/* Editor Content */}
      <div
        className={cn(
          'flex-1 overflow-y-auto bg-white',
          // Add hide classes for filtered entity types
          ...Array.from(['Problem', 'Person', 'Decision', 'Risk', 'Opportunity', 'ActionItem', 'Constraint'])
            .filter(type => !visibleEntityTypes.has(type))
            .map(type => `hide-type-${type.toLowerCase()}`)
        )}
      >
        <EditorContent editor={editor} className="h-full" />
      </div>

      {/* Slash Command Menu */}
      <SlashCommandMenu
        isOpen={slashCommand.isOpen}
        position={slashCommand.position}
        filter={slashCommand.filter}
        onSelect={handleCommandSelect}
        onClose={slashCommand.close}
      />

      {/* Detected Entity Tooltip */}
      <DetectedEntityTooltip
        entity={tooltipEntity}
        position={tooltipPosition}
        onAccept={(id) => acceptEntity(documentId, id)}
        onReject={(id) => rejectEntity(documentId, id)}
        onClose={() => {
          setTooltipEntity(null)
          setTooltipPosition(null)
        }}
      />
    </div>
  )
})

// Toolbar Button Component
interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>
  isActive?: boolean
  disabled?: boolean
  onClick: () => void
  title: string
}

function ToolbarButton({
  icon: Icon,
  isActive,
  disabled,
  onClick,
  title,
}: ToolbarButtonProps) {
  return (
    <button
      className={cn(
        'p-1.5 rounded transition-colors',
        isActive
          ? 'bg-warm-200 text-text-primary'
          : 'text-text-muted hover:bg-warm-100 hover:text-text-secondary',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}
